import React, { useEffect, useState } from 'react';
import {
    Button,
    Input,
    Space,
    Alert,
    message,
    Typography,
    Modal,
    Card,
    Select,
    Table,
    Tag,
    Collapse,
    Tooltip,
} from 'antd';
import {
    SaveOutlined,
    ReloadOutlined,
    PlusOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetQualificationConfigQuery,
    useGetQuestionTemplateTypesQuery,
    useUpdateSystemSettingMutation,
} from '../../services/nestjs-base-api';
import {
    IQualificationConfig,
    IQualificationOption,
    IQualificationCombination,
} from '../../services/nestjs-base-api/types';

const { Text, Paragraph } = Typography;

// Draft model — mirrors IQualificationConfig but keeps reverseDefaults as a list
// for easy add/remove editing (serialised back to an object on save).
interface ReverseDefaultRow {
    templateType: string;
    businessModel?: string;
    productCategory?: string;
    productCategoryLabel?: string;
}
interface Draft {
    businessModels: IQualificationOption[];
    productCategories: IQualificationOption[];
    combinations: Record<string, Record<string, IQualificationCombination>>;
    reverseDefaults: ReverseDefaultRow[];
}

const CELL_NONE = '__none__';
const CELL_WAITLIST = '__waitlist__';

function draftFromConfig(config: IQualificationConfig | null | undefined): Draft {
    return {
        businessModels: (config?.businessModels || []).map((m) => ({ ...m })),
        productCategories: (config?.productCategories || []).map((c) => ({ ...c })),
        combinations: JSON.parse(JSON.stringify(config?.combinations || {})),
        reverseDefaults: Object.entries(config?.reverseDefaults || {}).map(
            ([templateType, v]) => ({ templateType, ...v }),
        ),
    };
}

// Serialise the draft, pruning empty/orphaned entries so the stored config stays
// clean (e.g. a combination for a renamed/removed model is dropped).
function serialize(draft: Draft): IQualificationConfig {
    const businessModels = draft.businessModels
        .filter((m) => m.value.trim())
        .map((m) => ({ value: m.value.trim(), label: m.label.trim() }));
    const productCategories = draft.productCategories
        .filter((c) => c.value.trim())
        .map((c) => ({ value: c.value.trim(), label: c.label.trim() }));

    const modelValues = new Set(businessModels.map((m) => m.value));
    const catValues = new Set(productCategories.map((c) => c.value));

    const combinations: Record<string, Record<string, IQualificationCombination>> = {};
    for (const [model, cats] of Object.entries(draft.combinations)) {
        if (!modelValues.has(model)) continue;
        const inner: Record<string, IQualificationCombination> = {};
        for (const [cat, mapping] of Object.entries(cats || {})) {
            if (!catValues.has(cat) || !mapping) continue;
            inner[cat] = mapping;
        }
        if (Object.keys(inner).length) combinations[model] = inner;
    }

    const reverseDefaults: NonNullable<IQualificationConfig['reverseDefaults']> = {};
    for (const row of draft.reverseDefaults) {
        const tt = row.templateType?.trim();
        if (!tt) continue;
        const out: Record<string, string> = {};
        if (row.businessModel) out.businessModel = row.businessModel;
        if (row.productCategory?.trim()) out.productCategory = row.productCategory.trim();
        if (row.productCategoryLabel?.trim())
            out.productCategoryLabel = row.productCategoryLabel.trim();
        reverseDefaults[tt] = out;
    }

    const out: IQualificationConfig = { businessModels, productCategories, combinations };
    if (Object.keys(reverseDefaults).length) out.reverseDefaults = reverseDefaults;
    return out;
}

interface ValidationResult {
    errors: string[];
    warnings: string[];
}

// Validates the sync contract: every templateType a combination or reverse
// default resolves to must be an active registry row (else a brand landing
// there would have no benchmark group / label).
function validate(config: IQualificationConfig, registrySlugs: Set<string>): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    const seenModels = new Set<string>();
    for (const m of config.businessModels) {
        if (seenModels.has(m.value)) errors.push(`Duplicate business model value "${m.value}"`);
        seenModels.add(m.value);
    }
    const seenCats = new Set<string>();
    for (const c of config.productCategories) {
        if (seenCats.has(c.value)) errors.push(`Duplicate product category value "${c.value}"`);
        seenCats.add(c.value);
    }

    for (const [model, cats] of Object.entries(config.combinations)) {
        for (const [cat, mapping] of Object.entries(cats)) {
            if (mapping.supported === false) continue;
            if (!mapping.templateType) continue;
            if (!registrySlugs.has(mapping.templateType)) {
                warnings.push(
                    `${model} × ${cat} → "${mapping.templateType}" is not an active template type (Type Wiring). A brand resolving here would land in no benchmark group.`,
                );
            }
        }
    }
    for (const [tt] of Object.entries(config.reverseDefaults || {})) {
        if (!registrySlugs.has(tt)) {
            warnings.push(
                `Reverse default for "${tt}" is not an active template type (Type Wiring).`,
            );
        }
    }
    return { errors, warnings };
}

export const QualificationConfigEditor: React.FC = () => {
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';

    const { data: config, isLoading, refetch } = useGetQualificationConfigQuery();
    const { data: registry } = useGetQuestionTemplateTypesQuery({ jwtToken });
    const [updateSetting, { isLoading: isSaving }] = useUpdateSystemSettingMutation();

    const [draft, setDraft] = useState<Draft>(draftFromConfig(null));
    const [result, setResult] = useState<ValidationResult>({ errors: [], warnings: [] });
    const [rawText, setRawText] = useState('');

    useEffect(() => {
        if (config) setDraft(draftFromConfig(config));
    }, [config]);

    const activeTemplateTypes = (registry || []).filter((r) => r.isActive);
    const registrySlugs = new Set(activeTemplateTypes.map((r) => r.slug));

    // ---- Business models ----
    const updateBM = (idx: number, field: 'value' | 'label', val: string) =>
        setDraft((d) => {
            const businessModels = d.businessModels.map((m, i) =>
                i === idx ? { ...m, [field]: val } : m,
            );
            return { ...d, businessModels };
        });
    const addBM = () =>
        setDraft((d) => ({ ...d, businessModels: [...d.businessModels, { value: '', label: '' }] }));
    const removeBM = (idx: number) =>
        setDraft((d) => ({ ...d, businessModels: d.businessModels.filter((_, i) => i !== idx) }));

    // ---- Product categories ----
    const updatePC = (idx: number, field: 'value' | 'label', val: string) =>
        setDraft((d) => {
            const productCategories = d.productCategories.map((c, i) =>
                i === idx ? { ...c, [field]: val } : c,
            );
            return { ...d, productCategories };
        });
    const addPC = () =>
        setDraft((d) => ({
            ...d,
            productCategories: [...d.productCategories, { value: '', label: '' }],
        }));
    const removePC = (idx: number) =>
        setDraft((d) => ({ ...d, productCategories: d.productCategories.filter((_, i) => i !== idx) }));

    // ---- Combinations matrix ----
    const setCombination = (model: string, cat: string, outcome: string) =>
        setDraft((d) => {
            const combinations = JSON.parse(JSON.stringify(d.combinations)) as Draft['combinations'];
            if (!combinations[model]) combinations[model] = {};
            if (outcome === CELL_NONE) {
                delete combinations[model][cat];
                if (Object.keys(combinations[model]).length === 0) delete combinations[model];
            } else if (outcome === CELL_WAITLIST) {
                combinations[model][cat] = { supported: false };
            } else {
                const pt = activeTemplateTypes.find((t) => t.slug === outcome)?.productType;
                combinations[model][cat] = {
                    templateType: outcome,
                    ...(pt ? { productType: pt } : {}),
                };
            }
            return { ...d, combinations };
        });
    const cellValue = (model: string, cat: string): string => {
        const mapping = draft.combinations[model]?.[cat];
        if (mapping === undefined) return CELL_NONE;
        if (mapping.supported === false) return CELL_WAITLIST;
        return mapping.templateType || CELL_NONE;
    };
    const cellOptions = [
        { value: CELL_NONE, label: '— not offered' },
        { value: CELL_WAITLIST, label: 'Waitlist (capture demand)' },
        ...activeTemplateTypes.map((t) => ({ value: t.slug, label: t.label })),
    ];

    // ---- Reverse defaults ----
    const updateRD = (idx: number, field: keyof ReverseDefaultRow, val: string) =>
        setDraft((d) => {
            const reverseDefaults = d.reverseDefaults.map((r, i) =>
                i === idx ? { ...r, [field]: val } : r,
            );
            return { ...d, reverseDefaults };
        });
    const addRD = () =>
        setDraft((d) => ({ ...d, reverseDefaults: [...d.reverseDefaults, { templateType: '' }] }));
    const removeRD = (idx: number) =>
        setDraft((d) => ({ ...d, reverseDefaults: d.reverseDefaults.filter((_, i) => i !== idx) }));

    // ---- Save ----
    const doSave = async (parsed: IQualificationConfig) => {
        try {
            await updateSetting({
                jwtToken,
                settingName: 'QUALIFICATION_CONFIG',
                payload: { value: JSON.stringify(parsed) },
            }).unwrap();
            message.success('Qualification config saved');
            refetch();
        } catch (e: any) {
            message.error(e?.data?.message || 'Failed to save qualification config');
        }
    };

    const handleSave = () => {
        const parsed = serialize(draft);
        const r = validate(parsed, registrySlugs);
        setResult(r);
        if (r.errors.length > 0) {
            message.error('Fix the validation errors before saving');
            return;
        }
        if (r.warnings.length > 0) {
            Modal.confirm({
                title: 'Save with warnings?',
                content: `${r.warnings.length} mapping(s) resolve to a template type that isn't active in the registry. Save anyway?`,
                okText: 'Save anyway',
                onOk: () => doSave(parsed),
            });
            return;
        }
        doSave(parsed);
    };

    const businessModels = draft.businessModels;
    const productCategories = draft.productCategories;

    const matrixColumns = [
        {
            title: 'Product category',
            dataIndex: 'value',
            key: 'category',
            fixed: 'left' as const,
            width: 240,
            render: (_: any, cat: IQualificationOption) => (
                <div>
                    <div>{cat.label || <Text type="secondary">(no label)</Text>}</div>
                    <Tag style={{ marginTop: 4 }}>{cat.value}</Tag>
                </div>
            ),
        },
        ...businessModels
            .filter((m) => m.value.trim())
            .map((m) => ({
                title: (
                    <Tooltip title={m.value}>
                        <span>{m.label || m.value}</span>
                    </Tooltip>
                ),
                key: m.value,
                width: 200,
                render: (_: any, cat: IQualificationOption) => (
                    <Select
                        size="small"
                        style={{ width: '100%' }}
                        value={cellValue(m.value, cat.value)}
                        options={cellOptions}
                        onChange={(v) => setCombination(m.value, cat.value, v)}
                        disabled={!cat.value.trim()}
                    />
                ),
            })),
    ];

    return (
        <div>
            <Paragraph type="secondary">
                Drives the signup quiz. Ask category first; a business model is only
                asked when a category maps to more than one template. Every
                combination's template type must match an active row in{' '}
                <Text strong>Statement Templates → Type Wiring</Text>.
            </Paragraph>

            {result.errors.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Validation errors"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {result.errors.map((e, i) => (
                                <li key={i}>{e}</li>
                            ))}
                        </ul>
                    }
                />
            )}
            {result.warnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Registry sync warnings"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {result.warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                        </ul>
                    }
                />
            )}

            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* Business models */}
                <Card
                    size="small"
                    title="Business models"
                    extra={
                        <Button size="small" icon={<PlusOutlined />} onClick={addBM}>
                            Add
                        </Button>
                    }
                >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {businessModels.map((m, idx) => (
                            <Space key={idx} style={{ width: '100%' }} align="start">
                                <Input
                                    placeholder="VALUE (e.g. D2C)"
                                    value={m.value}
                                    style={{ width: 180 }}
                                    onChange={(e) => updateBM(idx, 'value', e.target.value)}
                                />
                                <Input
                                    placeholder="Label shown in the quiz"
                                    value={m.label}
                                    style={{ width: 360 }}
                                    onChange={(e) => updateBM(idx, 'label', e.target.value)}
                                />
                                <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeBM(idx)}
                                />
                            </Space>
                        ))}
                    </Space>
                </Card>

                {/* Product categories */}
                <Card
                    size="small"
                    title="Product categories"
                    extra={
                        <Button size="small" icon={<PlusOutlined />} onClick={addPC}>
                            Add
                        </Button>
                    }
                >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {productCategories.map((c, idx) => (
                            <Space key={idx} style={{ width: '100%' }} align="start">
                                <Input
                                    placeholder="VALUE (e.g. DURABLE_CONSUMER)"
                                    value={c.value}
                                    style={{ width: 240 }}
                                    onChange={(e) => updatePC(idx, 'value', e.target.value)}
                                />
                                <Input
                                    placeholder="Label shown in the quiz"
                                    value={c.label}
                                    style={{ width: 360 }}
                                    onChange={(e) => updatePC(idx, 'label', e.target.value)}
                                />
                                <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removePC(idx)}
                                />
                            </Space>
                        ))}
                    </Space>
                </Card>

                {/* Combinations matrix */}
                <Card
                    size="small"
                    title="Combinations (category × business model → outcome)"
                >
                    <Paragraph type="secondary" style={{ marginTop: 0 }}>
                        Pick each cell's outcome: a template type resolves the signup now,
                        “Waitlist” captures the demand, “not offered” hides the combination.
                    </Paragraph>
                    <Table
                        size="small"
                        rowKey="value"
                        pagination={false}
                        columns={matrixColumns}
                        dataSource={productCategories.filter((c) => c.value.trim())}
                        scroll={{ x: 'max-content' }}
                    />
                </Card>

                {/* Reverse defaults */}
                <Card
                    size="small"
                    title="Reverse defaults (templateType → business model / product category)"
                    extra={
                        <Button size="small" icon={<PlusOutlined />} onClick={addRD}>
                            Add
                        </Button>
                    }
                >
                    <Paragraph type="secondary" style={{ marginTop: 0 }}>
                        Canonical values attributed to an org created from a template type
                        alone (agency create-client, invite). Needed for templates whose
                        quiz categories don't collapse to one value (e.g. perishable → an
                        umbrella product category). The product category may be a synthetic
                        value not in the list above; its label goes in the last field.
                    </Paragraph>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {draft.reverseDefaults.map((r, idx) => (
                            <Space key={idx} style={{ width: '100%' }} align="start" wrap>
                                <Select
                                    placeholder="Template type"
                                    value={r.templateType || undefined}
                                    style={{ width: 180 }}
                                    options={activeTemplateTypes.map((t) => ({
                                        value: t.slug,
                                        label: t.label,
                                    }))}
                                    onChange={(v) => updateRD(idx, 'templateType', v)}
                                />
                                <Select
                                    placeholder="Business model"
                                    allowClear
                                    value={r.businessModel || undefined}
                                    style={{ width: 180 }}
                                    options={businessModels
                                        .filter((m) => m.value.trim())
                                        .map((m) => ({ value: m.value, label: m.label || m.value }))}
                                    onChange={(v) => updateRD(idx, 'businessModel', v || '')}
                                />
                                <Input
                                    placeholder="Product category value"
                                    value={r.productCategory || ''}
                                    style={{ width: 220 }}
                                    onChange={(e) => updateRD(idx, 'productCategory', e.target.value)}
                                />
                                <Input
                                    placeholder="Product category label"
                                    value={r.productCategoryLabel || ''}
                                    style={{ width: 240 }}
                                    onChange={(e) =>
                                        updateRD(idx, 'productCategoryLabel', e.target.value)
                                    }
                                />
                                <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeRD(idx)}
                                />
                            </Space>
                        ))}
                    </Space>
                </Card>

                {/* Advanced: raw JSON */}
                <Collapse
                    items={[
                        {
                            key: 'raw',
                            label: 'Advanced — raw JSON',
                            children: (
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                    <Space>
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                setRawText(JSON.stringify(serialize(draft), null, 2))
                                            }
                                        >
                                            Load current builder state
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                try {
                                                    const parsed = JSON.parse(
                                                        rawText,
                                                    ) as IQualificationConfig;
                                                    setDraft(draftFromConfig(parsed));
                                                    message.success('Applied to builder');
                                                } catch (e: any) {
                                                    message.error(`Invalid JSON: ${e.message}`);
                                                }
                                            }}
                                        >
                                            Apply to builder
                                        </Button>
                                    </Space>
                                    <Input.TextArea
                                        value={rawText}
                                        onChange={(e) => setRawText(e.target.value)}
                                        autoSize={{ minRows: 10, maxRows: 40 }}
                                        spellCheck={false}
                                        style={{ fontFamily: 'monospace', fontSize: 13 }}
                                    />
                                </Space>
                            ),
                        },
                    ]}
                />

                <Space>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={isSaving}
                        disabled={isLoading}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            setDraft(draftFromConfig(config));
                            setResult({ errors: [], warnings: [] });
                        }}
                    >
                        Revert
                    </Button>
                </Space>
            </Space>
        </div>
    );
};
